<template>
  <div
    class="account-group-list-item__container"
    :class="containerClass"
    :data-testid="`account-group-list-item-${itemId}`"
    @click="handleRowClick"
    @mouseover="handleMouseOver"
    @mouseleave="handleMouseOut"
  >
    <div>
      <div :class="itemClass">
        <span
          v-if="showChevron"
          class="sf-c-text-darker"
          :class="chevronClass"
          data-testid="account-group-toggle-chevron"
          @click.stop="toggleChevron"
        />
        <div
          class="
            account-group-list-item__content-container
            sf-pointer sf-level-item-full
          "
        >
          <div
            class="
              account-group-list-item__group-info
              sf-level-item-full sf-font-s sf-font-normal
            "
            :class="groupInfoExtraClass"
          >
            <i
              v-if="isAiLeaf && !isExternallyMapped"
              class="fa fa-wand-magic-sparkles account-group-list-item__icon"
              data-testid="ai-proposal-icon"
            />
            <ExternallyMappedIcon
              v-if="isExternallyMapped"
              class="account-group-list-item__icon"
            />
            <div class="sf-level sf-level-item-full sf-level-between">
              <div>
                <span
                  v-if="item.accountRange && !searchNumberHighlight"
                  class="sf-line-height-2"
                >
                  {{ item.accountRange }}
                </span>
                <SearchTextHighlight
                  v-if="item.accountRange && searchNumberHighlight"
                  :fuzzy-result="searchNumberHighlight"
                />

                <span v-if="!searchNameHighlight" class="sf-line-height-2">
                  {{ displayName }}
                </span>
                <SearchTextHighlight
                  v-if="searchNameHighlight"
                  :fuzzy-result="searchNameHighlight"
                />
              </div>
              <div
                v-if="itemId == getFlatMappedAccounts[focusedDropdown]?.groupId"
                class="sf-c-primary"
              >
                <i class="fa fa-check" data-testid="current-mapping-icon" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'pinia'
import ExternallyMappedIcon from './externally-mapped-icon.vue'
import { pickAccountNameByLocale } from '@app/application/account-mapping/utils'
import SearchTextHighlight from '@app/application/account-mapping/components/search-text-highlight'
import { useCommon } from '@app/application/account-mapping/store/common'
import { useSearch } from '@app/application/account-mapping/store/search'
import { MAPPING_SOURCES } from '@app/application/account-mapping/constants'

import { useLocale } from '@app/application/account-mapping/store/locale'
import { useGroupList } from '@app/application/account-mapping/store/group-list'

export default {
  components: {
    SearchTextHighlight,
    ExternallyMappedIcon,
  },
  props: {
    isExternallyMapped: {
      type: Boolean,
      required: false,
    },
    isCollapsed: {
      type: Boolean,
    },
    item: {
      type: Object,
      required: true,
    },
    itemId: {
      type: String,
      required: true,
    },
    mappedAccounts: {
      type: Object,
      default: function () {
        return {}
      },
    },
    searchHighlight: {
      type: Object,
      default: function () {
        return {}
      },
    },
  },
  emits: ['item-clicked'],
  data() {
    return {
      hover: false,
      MAPPING_SOURCES,
    }
  },
  computed: {
    ...mapState(useCommon, [
      'groupHierarchy',
      'getFlatMappedAccounts',
      'recommendations',
    ]),
    ...mapState(useSearch, ['remapDropdownShowGroup']),
    ...mapState(useGroupList, ['focusedDropdown']),
    ...mapState(useLocale, ['supportedLocales']),
    chevronClass: function () {
      return {
        'fa fa-chevron-down': !this.isCollapsed,
        [`icon-chevron-indent-${this.indent}`]: true,
        'fa fa-chevron-right': this.isCollapsed,
        'account-group-list-item__chevron--right': this.isCollapsed,
        icon: true,
        'sf-c-text-light': true,
        'sf-pointer': true,
        'account-group-list-item__chevron': true,
        'account-group-list-item__chevron--hidden':
          this.item.accountRange && this.mappedAccountsLength === 0,
      }
    },
    containerClass: function () {
      return {
        'account-group-list-item__container--expanded': this.isExpandedLeaf,
      }
    },
    currentHierarchy: function () {
      return this.groupHierarchy[this.itemId]
    },
    displayName: function () {
      return pickAccountNameByLocale({
        account: this.item,
        noNamePrefix: true,
        supportedLocales: this.supportedLocales,
      })
    },
    indent: function () {
      return this.currentHierarchy.level + 1
    },
    isAiLeaf: function () {
      return (
        this.recommendationProposals.some(
          (p) =>
            p.accountRange === this.item.accountRange &&
            p.source === this.MAPPING_SOURCES.AI
        ) && this.item.accountRange
      )
    },
    isExpandedLeaf: function () {
      return (
        this.item.accountRange &&
        !this.isCollapsed &&
        this.mappedAccountsLength > 0
      )
    },
    itemClass: function () {
      return {
        'account-group-list-item__inner-container': true,
        'account-group-list-item__inner-container--expanded':
          this.isExpandedLeaf,
        [`account-group-list-item__inner-container--indent-${this.indent}`]: true,
      }
    },
    mappedAccountsArray: function () {
      return Object.keys(this.mappedAccounts)
    },
    mappedAccountsLength: function () {
      return this.mappedAccountsArray.length
    },
    groupInfoExtraClass: function () {
      return {
        'account-group-list-item__group-info--leaf': this.item.accountRange,
      }
    },
    showChevron: function () {
      return !this.item.accountRange
    },
    searchNumberHighlight: function () {
      if (!this.searchHighlight?.highlights?.preparedAccountRange) {
        return false
      }

      return this.searchHighlight.highlights.preparedAccountRange
    },
    searchNameHighlight: function () {
      if (!this.searchHighlight?.highlights?.preparedName) {
        return false
      }

      return this.searchHighlight.highlights.preparedName
    },
    recommendation: function () {
      return this.recommendations[this.focusedDropdown]
    },
    recommendationProposals: function () {
      return this.recommendation?.proposals || []
    },
  },
  methods: {
    ...mapActions(useGroupList, ['toggleGroup']),
    handleMouseOut: function () {
      this.hover = false
    },
    handleMouseOver: function () {
      this.hover = true
    },
    handleRowClick: function () {
      if (!this.item.accountRange) return
      this.$emit('item-clicked', {
        id: this.itemId,
        accountRange: this.item.accountRange,
      })
    },
    toggleChevron: function () {
      if (this.item.accountRange && this.mappedAccountsLength === 0) return

      this.toggleGroup(this.itemId)
    },
  },
}
</script>

<style scoped lang="scss">
@use 'sass:math';
@import 'silverfin-ui-kit/src/scss/tooling';

@function indent($level) {
  @return ($level - 1) * space(3);
}

@mixin indent-padding($level) {
  padding: space(1) 0 space(1) indent($level);
}

.account-group-list-item {
  &__chevron {
    margin-right: math.div(space(1), 2);
    height: 16px;
    width: 16px;
    font-size: 14px;

    &--hidden {
      visibility: hidden;
    }

    &--right {
      width: 16px;
    }
  }

  &__container {
    border-left: 1px solid -color('white');

    &--full {
      border-bottom: 1px solid -color('snow', 'dark');
    }
  }

  &__content-container {
    display: flex;
    align-items: center;
  }

  &__group {
    &-info {
      display: flex;
      align-items: center;
      position: relative;
    }

    &-range {
      margin: 0 10px 0 0;
    }

    &-total {
      margin: 0 27px 0 0;
      line-height: 1;
    }

    &-name {
      @include font-weight('medium');

      margin: 0 10px 0 0;
      font-size: 14px;
      line-height: 17px;

      &--indent-1 {
        @include font-size('heading', 's');

        line-height: 26px;
      }

      &--indent-2 {
        font-size: 16px;
        line-height: 24px;
      }
    }
  }

  &__icon {
    position: absolute;
    left: -22px;
    top: 0;
  }

  &__inner-container {
    @include color('text', 'darker');
    @include indent-padding(5);

    display: flex;
    align-items: center;
    line-height: 16px;

    &--indent-1 {
      @include color('text');

      padding: space(1) 0 space(1) 4px;
    }

    &--indent-2 {
      @include color('text', 'dark');

      @include indent-padding(2);
    }

    &--indent-3 {
      @include indent-padding(3);
    }

    &--indent-4 {
      @include indent-padding(4);
    }

    &--indent-5 {
      @include indent-padding(5);
    }
  }

  &__unsaved {
    margin: 0 0 0 auto;
  }
}
</style>
